import createSemaphore from "./semaphore";
import runtimeOverride from "./runtimeOverride";
import { throwIfCancelled } from "./cancelled";

// One queue for every file input on the page.
//
// A Data Collection entry can hold dozens of file inputs — jobs in production ask for 70 videos
// in a single entry — and each input starts its upload the moment its file is picked. Left to
// themselves that is 70 uploads running at once: 70 streaming hashes each holding a 64 MB slice
// of a multi-GB file, and 70 x 6 concurrent part PUTs sharing one uplink. The browser runs out of
// memory, and the contributor's bandwidth is split so many ways that no single file finishes.
//
// It is the client that breaks, not the bucket: DigitalOcean reviewed a week of activity on both
// crowd (nyc3) and crowd-europe (ams3) and found no rate limiting or throttling of ours. Spaces
// does document a ceiling of 800 operations per second per bucket, which this keeps us far inside,
// but staying inside it was never the binding reason to queue.
//
// So the work is queued instead. Six files are on the wire at a time, and while they are, the
// next six are being hashed — so a file reaches the front of the queue with its uniqueness check
// already done and goes straight onto the network rather than stopping to read itself first.
// The hashing is not allowed to run further ahead than that: without a bound, picking 70 files
// would read all 70 end to end while the first ones uploaded, which is the same disk and memory
// thrashing in a different order.

const DEFAULT_MAX_CONCURRENT_FILES = 6;

// How many files at a time is best depends on the contributor's connection, which we cannot see
// from here — so it is a runtime knob rather than a constant, and it can be turned down as
// readily as up on a machine that does not cope.
//   ?uploadFileConcurrency=2   or   localStorage.setItem("crowd.uploadFileConcurrency", "2")
function maxConcurrentFiles() {
    return runtimeOverride({
        queryKey: "uploadFileConcurrency",
        storageKey: "crowd.uploadFileConcurrency",
        fallback: DEFAULT_MAX_CONCURRENT_FILES,
        max: 8,
    });
}

const uploadSlots = createSemaphore(maxConcurrentFiles);
const hashSlots = createSemaphore(maxConcurrentFiles);
const pipelineSlots = createSemaphore(() => maxConcurrentFiles() * 2);

// Every queued upload, in the order its file was picked. Position in here is what a contributor
// waiting their turn is told, so it has to be the pick order and not the order tasks happen to
// reach a semaphore.
const queue = [];

function publishPositions() {
    queue.forEach((task, index) => {
        const position = index + 1;
        // Only a task that is genuinely waiting gets told a position. One that is hashing or
        // uploading is already reporting its own progress, and overwriting that with "waiting"
        // every time a sibling finishes would make the bar flicker between the two. Nor is the
        // one at the front waiting on anybody — it is about to start, and saying "1st in line"
        // in the instant before it does is a flash of text nobody can read.
        if (index === 0 || !task.waiting || task.position === position) {
            return;
        }
        task.position = position;
        try {
            task.onQueued && task.onQueued(position);
        }
        catch (e) {
            console.error("Could not report the upload queue position.", e);
        }
    });
}

function leaveQueue(task) {
    const index = queue.indexOf(task);
    if (index !== -1) {
        queue.splice(index, 1);
    }
}

// 1st, 2nd, 3rd, 4th… Lives here rather than in the five file inputs that show it, so they all
// word the wait the same way.
export function queuePositionLabel(position) {
    const suffix = position % 100 >= 11 && position % 100 <= 13
        ? "th"
        : { 1: "st", 2: "nd", 3: "rd" }[position % 10] || "th";
    return `Waiting for other uploads… ${position}${suffix} in line`;
}

// `hash` and `upload` are the two stages, kept apart so the queue can run one ahead of the other.
// `onQueued(position)` is called whenever this upload is waiting and its place in line changes.
// `signal` cancels it at whatever point it has reached, including while it waits its turn.
export default async function enqueueUpload({ hash, upload, onQueued, signal }) {
    const task = { onQueued, waiting: true, position: null };
    queue.push(task);
    publishPositions();

    let releasePipeline = () => {};
    try {
        releasePipeline = await pipelineSlots.acquire(signal);
        const releaseHash = await hashSlots.acquire(signal);
        let hashed;
        try {
            throwIfCancelled(signal);
            task.waiting = false;
            hashed = await hash();
        }
        finally {
            releaseHash();
        }

        // Hashed but not yet on the wire: back to waiting, and the contributor is told so, because
        // on a big entry this is where most of the waiting actually happens.
        task.waiting = true;
        task.position = null;
        publishPositions();

        const releaseUpload = await uploadSlots.acquire(signal);
        try {
            throwIfCancelled(signal);
            task.waiting = false;
            return await upload(hashed);
        }
        finally {
            releaseUpload();
        }
    }
    finally {
        leaveQueue(task);
        releasePipeline();
        publishPositions();
    }
}

// Test seam: the queue is module state shared by the whole page, so a test that leaves work in it
// would leak into the next one.
export function __resetQueueForTests() {
    queue.length = 0;
}
